import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TablePagination,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import Navbar from '../../globals/components/Navbar';
import { apiClient } from '../../api/httpClient';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { setPgBondsFilters, setPgBondsPage, resetPgBondsState } from '../../store/slices/pgBondsSlice';
import TablePaginationActions from "../../globals/components/TablePaginationActions";
import {
  PG_BONDS_BOND_TYPE_TO_SUBCATEGORIES,
  PG_BONDS_DWB_LABEL_MAP,
  createEmptyPgBondsFilters,
  getPgBondsTableColumns,
  getReleaseRiderUrl,
  isInternalOrSuperRole,
  isNameChangeEndorsement,
  isNameChangeValidationOk,
  transformPgBondStatusForDisplay,
} from './pgBondsTableConfig';
import { formatUpdatedBondAmountForEndorsement } from '../Law Firm User/helpers/lawFirmBondsHelpers';
import {
  PgBondsCancellationDialog,
  PgBondsDataTable,
  PgBondsEndorsementDialog,
  PgBondsFilterDialog,
  getBondUpdateUrl,
  validatePgBondDocumentFile,
} from './PgBondsTableBase';
import {
  buildRiderEndorsementDraftFromDialog,
  navigateToReleaseRider,
} from '../components/endorsement/endorsementSubmitHelpers';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

function ProfessionalGuardianViewBonds() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [searchQuery] = useState(''); // eslint-disable-line no-unused-vars
  const savedPage = useSelector((state) => state.pgBonds?.page ?? 0);
  const [page, setPage] = useState(savedPage);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [allBonds, setAllBonds] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const { id } = useParams();
  const { role } = useSelector((state) => state.auth);
  const savedFilters = useSelector((state) => state.pgBonds?.filters) || createEmptyPgBondsFilters();

  const userdetails = useSelector((state) => state.auth?.user);

  const [filters, setFilters] = useState(savedFilters);

  const [bondTypes, setBondTypes] = useState([]);
  const [lawFirms, setLawFirms] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [estates, setEstates] = useState([]);
  const [principals, setPrincipals] = useState([]);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBondToCancel, setSelectedBondToCancel] = useState(null);
  const [cancellationFile, setCancellationFile] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const [endorsementDialogOpen, setEndorsementDialogOpen] = useState(false);
  const [selectedBondToEndorse, setSelectedBondToEndorse] = useState(null);
  const [endorsementType, setEndorsementType] = useState("Increase");
  const [newBondAmount, setNewBondAmount] = useState("");
  const [endorsementFile, setEndorsementFile] = useState(null);
  const [isSubmittingEndorsement, setIsSubmittingEndorsement] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [updatedName, setUpdatedName] = useState("");
  const [counties, setCounties] = useState([]);
  const [endorsementToCounty, setEndorsementToCounty] = useState("");
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [estimatedAssets, setEstimatedAssets] = useState("");
  const [dwbFirstName, setDwbFirstName] = useState("");
  const [dwbMiddleName, setDwbMiddleName] = useState("");
  const [dwbLastName, setDwbLastName] = useState("");
  const [dwbSuffix, setDwbSuffix] = useState("");
  const [dwbAlias, setDwbAlias] = useState("");
  const [endorsementPrincipalId, setEndorsementPrincipalId] = useState("");

  useEffect(() => {
    if (!userdetails) return;
    const name = [userdetails.first_name, userdetails.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    setContactName(name);
    setContactEmail(userdetails.email ?? "");
  }, [userdetails]);

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (filters.lawFirm.length > 0) {
      const firmIds = filters.lawFirm.map((f) => f.value);
      Promise.all(
        firmIds.map((firmId) => apiClient(`/lawfirm-user-location/${firmId}`, "GET"))
      ).then((responses) => {
        const merged = [].concat(...responses)
          .filter((a) => a.Position === "Attorney")
          .map((a) => ({
            label: a.lawfirm_user_name,
            value: a.lawfirm_user_id,
          }));
        setAttorneys(merged);
      });
    } else {
      setAttorneys([]);
    }
  }, [filters.lawFirm]);

  const fetchInitialData = async (overrideFilters = null) => {
    if (!id) return;
    setLoading(true);
    try {
      const guardianBondsData = await apiClient(`/list-guardian-bonds/${id}/`, 'GET');
      const rawList = Array.isArray(guardianBondsData) ? guardianBondsData : [];

      const filteredBonds = rawList.map((bond) => ({
        bond_id: bond.bond_id,
        bond_number: bond.bond_number || '',
        bond_type: bond.bond_type,
        bond_sub_category: bond.bond_sub_category || '',
        principals: Array.isArray(bond.principals) ? bond.principals : [],
        estate_name: bond.name,
        law_firm_name: bond.law_firm_name,
        attorney_name: bond.law_firm_user_name,
        underwriting_status: bond.underwriting_status || '-',
        bond_amount: bond.bond_amount || '0.00',
        managed_by: (bond.managed_by || []).map((u) => (typeof u === 'object' && u?.full_name ? u.full_name : u)),
        status: bond.status || 'Active',
        created_at: bond.created_at,
        principal_one: Array.isArray(bond.principals) ? bond.principals[0] : '',
      }));
      
      const [bt, lf] = await Promise.all([
        apiClient("/bondtypes/", "GET"),
        apiClient("/list-lawfirms/", "GET")
      ]);

      setBondTypes(
        bt
          .filter((b) => b.type !== "Guardian-Minor")
          .map((b) => ({ label: b.type, value: b.type }))
      );
      
      const distinctLawFirmNames = [...new Set(
        filteredBonds.map(bond => bond.law_firm_name).filter(Boolean)
      )];
      
      const filteredLawFirms = lf.filter(firm => 
        distinctLawFirmNames.includes(firm.name)
      ).map(f => ({ label: f.name, value: f.lawfirm_id }));
      
      setLawFirms(filteredLawFirms);

      if (isInternalOrSuperRole(role)) {
        const iu = await apiClient("/internal-user", "GET");
        setInternalUsers(
          (iu?.data || []).map((u) => ({ label: u.full_name, value: u.internal_user_id }))
        );
      } else {
        setInternalUsers([]);
      }

      const distinctPrincipals = [...new Set(
        filteredBonds.flatMap(bond => bond.principals)
      )].filter(Boolean).map((principal, index) => ({
        label: principal,
        bondType: filteredBonds.find(bond => bond.principals.includes(principal))?.bond_type || '',
        value: `${principal}-${filteredBonds.find(bond => bond.principals.includes(principal))?.bond_type || ''}-${index}`
      }));

      const distinctEstates = [...new Set(
        filteredBonds.map(bond => bond.estate_name).filter(Boolean)
      )].map((estate, index) => ({
        label: estate,
        bondType: filteredBonds.find(bond => bond.estate_name === estate)?.bond_type || '',
        value: `${estate}-${filteredBonds.find(bond => bond.estate_name === estate)?.bond_type || ''}-${index}`
      }));

      setPrincipals(distinctPrincipals);
      setEstates(distinctEstates);

      const reversed = [...filteredBonds].reverse();
      setAllBonds(reversed);

      let result = [...reversed];
      const f = overrideFilters ?? filters ?? savedFilters;

      if (f.bondNumber?.length)
        result = result.filter((b) => f.bondNumber.some((x) => b.bond_number === x.value));

      if (f.startDate) {
        const start = dayjs(f.startDate).startOf('day');
        result = result.filter((b) => dayjs(b.created_at).isSameOrAfter(start, 'day'));
      }

      if (f.endDate) {
        const end = dayjs(f.endDate).endOf('day');
        result = result.filter((b) => dayjs(b.created_at).isSameOrBefore(end, 'day'));
      }

      if (f.bondType?.length)
        result = result.filter((b) => f.bondType.some((x) => x.value === b.bond_type));

      if (f.bondSubCategory?.length)
        result = result.filter((b) => f.bondSubCategory.includes(b.bond_sub_category));

      if (f.principal?.length)
        result = result.filter((b) => f.principal.some((x) => b.principals.includes(x.label)));

      if (f.estate?.length)
        result = result.filter((b) => f.estate.some((x) => b.estate_name === x.label));

      if (f.lawFirm?.length)
        result = result.filter((b) => f.lawFirm.some((x) => b.law_firm_name === x.label));

      if (f.attorney?.length)
        result = result.filter((b) => f.attorney.some((x) => b.attorney_name === x.label));

      if (f.internalUser?.length && isInternalOrSuperRole(role))
        result = result.filter((b) => f.internalUser.some((x) => b.managed_by.includes(x.label)));

      if (f.status?.length)
        result = result.filter((b) => f.status.includes(b.status));

      setFilteredData(result);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching bonds:", error);
      setLoading(false);
    }
  };

  const sortRows = (rows) => {
    return [...rows].sort((a, b) => {
      if (orderBy === 'bond_amount') {
        const valA = parseFloat(a.bond_amount);
        const valB = parseFloat(b.bond_amount);
        return order === 'asc' ? valA - valB : valB - valA;
      } else if (orderBy === 'created_at') {
        return order === 'asc'
          ? new Date(a.created_at) - new Date(b.created_at)
          : new Date(b.created_at) - new Date(a.created_at);
      } else {
        const valA = a[orderBy]?.toString().toLowerCase() || '';
        const valB = b[orderBy]?.toString().toLowerCase() || '';
        return order === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
    });
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const applyFilters = () => {
    let result = [...allBonds];

    if (filters.bondNumber.length)
      result = result.filter((b) =>
        filters.bondNumber.some((f) => b.bond_number === f.value)
      );

    if (filters.startDate) {
      const start = dayjs(filters.startDate).startOf("day");
      result = result.filter((b) =>
        dayjs(b.created_at).isSameOrAfter(start, "day")
      );
    }

    if (filters.endDate) {
      const end = dayjs(filters.endDate).endOf("day");
      result = result.filter((b) =>
        dayjs(b.created_at).isSameOrBefore(end, "day")
      );
    }

    if (filters.bondType.length)
      result = result.filter((b) =>
        filters.bondType.some((f) => f.value === b.bond_type)
      );

    if (filters.bondSubCategory.length)
      result = result.filter((b) =>
        filters.bondSubCategory.includes(b.bond_sub_category)
      );

    if (filters.principal.length)
      result = result.filter((b) =>
        filters.principal.some((f) => b.principals.includes(f.label))
      );

    if (filters.estate.length)
      result = result.filter((b) =>
        filters.estate.some((f) => b.estate_name === f.label)
      );

    if (filters.lawFirm.length)
      result = result.filter((b) =>
        filters.lawFirm.some((f) => b.law_firm_name === f.label)
      );

    if (filters.attorney.length)
      result = result.filter((b) =>
        filters.attorney.some((f) => b.attorney_name === f.label)
      );

    if (filters.internalUser.length && isInternalOrSuperRole(role))
      result = result.filter((b) =>
        filters.internalUser.some((f) => b.managed_by.includes(f.label))
      );

    if (filters.status.length)
      result = result.filter((b) => {
        if (filters.status.includes("Active")) {
          const otherStatuses = filters.status.filter(s => s !== "Active");
          return b.status === "Active" || b.status === "Approved" || otherStatuses.includes(b.status);
        }
        return filters.status.includes(b.status);
      });

    setFilteredData(result);
    setFilterDialogOpen(false);
    setPage(0);
    dispatch(setPgBondsFilters(filters));
    dispatch(setPgBondsPage(0));
  };

  const clearFilters = () => {
    const cleared = createEmptyPgBondsFilters();
    setFilters(cleared);
    dispatch(setPgBondsFilters(cleared));
    setPage(0);
    dispatch(setPgBondsPage(0));
  };

  const bondStateId = selectedBondToEndorse?.state ?? 6;
  useEffect(() => {
    if (!endorsementDialogOpen || !bondStateId) return;
    const fetchCounties = async () => {
      setLoadingCounties(true);
      try {
        const countyResponse = await apiClient(
          `/counties-by-state/${bondStateId}/`,
          "GET"
        );
        const formatted = (countyResponse || []).map((c) => ({
          label: c.bond_county,
          value: c.bond_county,
        }));
        setCounties(formatted);
      } catch {
        setCounties([]);
      } finally {
        setLoadingCounties(false);
      }
    };
    fetchCounties();
  }, [endorsementDialogOpen, bondStateId]);

  const searchFilteredData = filteredData.filter((bond) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      bond.principals.some((p) => p.toLowerCase().includes(query)) ||
      bond.bond_number?.toLowerCase().includes(query) ||
      bond.estate_name?.toLowerCase().includes(query) ||
      bond.law_firm_name?.toLowerCase().includes(query) ||
      bond.attorney_name?.toLowerCase().includes(query) ||
      (isInternalOrSuperRole(role) && bond.managed_by.some((m) => m.toLowerCase().includes(query))) ||
      transformPgBondStatusForDisplay(bond.status)?.toLowerCase().includes(query)
    );
  });

  const sortedData = sortRows(searchFilteredData);
  const currentRows = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    dispatch(setPgBondsPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    dispatch(setPgBondsPage(0));
  };

  const tableColumns = getPgBondsTableColumns(role);

  const selectedBondTypes = filters.bondType.map((b) => b.value);

  const availableSubcategories = selectedBondTypes.flatMap(
    (type) => PG_BONDS_BOND_TYPE_TO_SUBCATEGORIES[type] || []
  );

  const availablePrincipals = selectedBondTypes.length
    ? principals.filter((p) => selectedBondTypes.includes(p.bondType))
    : principals;

  const availableEstates = selectedBondTypes.length
    ? estates.filter((e) => selectedBondTypes.includes(e.bondType))
    : estates;

  const resetEndorsementForm = (bond) => {
    setSelectedBondToEndorse(bond);
    setEndorsementType("Increase");
    setNewBondAmount("");
    setEndorsementFile(null);
    setUpdatedName("");
    setEndorsementToCounty("");
    setEstimatedAssets("");
    setDwbFirstName("");
    setDwbMiddleName("");
    setDwbLastName("");
    setDwbSuffix("");
    setDwbAlias("");
    setEndorsementPrincipalId("");
    setEndorsementDialogOpen(true);
  };

  const buildEndorsementFormData = () => {
    const formData = new FormData();
    formData.append("endorsement_type", endorsementType);
    if (isNameChangeEndorsement(endorsementType)) {
      if (endorsementType === "Name_Change_County") {
        formData.append("updated_name", (endorsementToCounty || "").trim().split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" "));
      } else if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
        if (PG_BONDS_DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single) {
          formData.append("updated_name", updatedName);
        } else {
          formData.append("updated_name", [dwbFirstName, dwbMiddleName, dwbLastName, dwbSuffix].filter(Boolean).join(" "));
          formData.append("updated_first_name", dwbFirstName);
          formData.append("updated_middle_name", dwbMiddleName);
          formData.append("updated_last_name", dwbLastName);
          formData.append("updated_suffix", dwbSuffix);
          formData.append("updated_alias", dwbAlias);
        }
      } else if (endorsementType === "Name_Change_Principal") {
        formData.append("updated_name", updatedName);
        if (endorsementPrincipalId) {
          formData.append("principal_id", String(endorsementPrincipalId));
          if (selectedBondToEndorse?.bond_type === "Trustee") {
            formData.append("trustee_id", String(endorsementPrincipalId));
          }
        }
      } else {
        formData.append("updated_name", updatedName);
      }
    } else {
      formData.append(
        "updated_bond_amount",
        formatUpdatedBondAmountForEndorsement(newBondAmount)
      );
    }
    if (endorsementFile) formData.append("endorsement_file", endorsementFile);
    formData.append("contact_name", contactName);
    formData.append("contact_email", contactEmail);
    const estAssetsNum = estimatedAssets !== "" && !Number.isNaN(Number(estimatedAssets.replace(/[^\d.]/g, ""))) ? Number(estimatedAssets.replace(/[^\d.]/g, "")) : null;
    if (
      (selectedBondToEndorse?.bond_type === "Probate" ||
        selectedBondToEndorse?.bond_type === "Guardianship") &&
      estAssetsNum != null
    ) {
      formData.append("estimated_assets", estAssetsNum);
    }
    return formData;
  };

  const getNameChangeValidation = () =>
    isNameChangeValidationOk({
      endorsementType,
      endorsementToCounty,
      updatedName,
      bondType: selectedBondToEndorse?.bond_type,
      dwbFirstName,
      dwbLastName,
    });

  const handleReleaseRider = async () => {
    if (!selectedBondToEndorse) return;
    const riderUrl = getReleaseRiderUrl(selectedBondToEndorse);
    const nameChangeOk = getNameChangeValidation();
    const hasDetails = endorsementType &&
      contactName &&
      contactEmail &&
      (isNameChangeEndorsement(endorsementType) ? nameChangeOk : (!!newBondAmount && !!endorsementFile));

    if (isInternalOrSuperRole(role) && hasDetails) {
      setIsSubmittingEndorsement(true);
      try {
        await apiClient(
          `/endorse-bond/`,
          "POST",
          buildEndorsementFormData(),
          { bond_id: selectedBondToEndorse.bond_id },
          { "Content-Type": "multipart/form-data" }
        );
        toast.success("Bond endorsed successfully!");
        setEndorsementDialogOpen(false);
        await fetchInitialData();
        navigate(riderUrl);
      } catch (error) {
        console.error("Endorsement failed:", error);
        toast.error(error.message || "Failed to endorse the bond.");
      } finally {
        setIsSubmittingEndorsement(false);
      }
    } else if (riderUrl) {
      const draft = buildRiderEndorsementDraftFromDialog({
        endorsementType,
        newBondAmount,
        selectedBondToEndorse,
      });
      navigateToReleaseRider(
        navigate,
        riderUrl,
        draft ? { riderEndorsementDraft: draft } : undefined
      );
    }
  };

  const handleSubmitEndorsement = async () => {
    const nameChangeOk = getNameChangeValidation();
    if (
      !selectedBondToEndorse ||
      (!isNameChangeEndorsement(endorsementType) && !endorsementFile) ||
      (isNameChangeEndorsement(endorsementType) ? !nameChangeOk : !newBondAmount) ||
      !endorsementType ||
      !contactName ||
      !contactEmail
    ) {
      toast.error(
        isNameChangeEndorsement(endorsementType)
          ? "Please fill all required fields and contact info."
          : "Please fill all required fields, contact info, and upload the file."
      );
      return;
    }

    setIsSubmittingEndorsement(true);
    try {
      await apiClient(
        `/endorse-bond/`,
        "POST",
        buildEndorsementFormData(),
        { bond_id: selectedBondToEndorse.bond_id },
        { "Content-Type": "multipart/form-data" }
      );
      toast.success("Bond endorsed successfully!");
      setEndorsementDialogOpen(false);
      await fetchInitialData();
    } catch (error) {
      console.error("Endorsement failed:", error);
      toast.error(error.message || "Failed to endorse the bond.");
    } finally {
      setIsSubmittingEndorsement(false);
    }
  };

  return (
    <>
      <Navbar />
      <Box sx={{ maxWidth: '100%', padding: '20px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Professional Guardian Bonds
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              onClick={() => {
                const cleared = createEmptyPgBondsFilters();
                setFilters(cleared);
                setPage(0);
                dispatch(resetPgBondsState());
                fetchInitialData(cleared);
              }}
              startIcon={<RefreshIcon />}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
            <Button
              variant="contained"
              onClick={() => setFilterDialogOpen(true)}
              startIcon={<FilterListIcon />}
            >
              Filters
            </Button>
          </Box>
        </Box>

        <PgBondsDataTable
          loading={loading}
          tableColumns={tableColumns}
          orderBy={orderBy}
          order={order}
          handleSort={handleSort}
          searchFilteredData={searchFilteredData}
          currentRows={currentRows}
          role={role}
          onViewBond={(bond) => {
            const url = getBondUpdateUrl(bond);
            if (url) window.open(url, '_blank');
          }}
          onCancelBond={(bond) => {
            setSelectedBondToCancel(bond);
            setCancellationFile(null);
            setCancelDialogOpen(true);
          }}
          onEndorseBond={resetEndorsementForm}
        />

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={searchFilteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 2 }}
        />

        <PgBondsFilterDialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          filters={filters}
          setFilters={setFilters}
          applyFilters={applyFilters}
          clearFilters={clearFilters}
          bondTypes={bondTypes}
          availableSubcategories={availableSubcategories}
          allBonds={allBonds}
          availablePrincipals={availablePrincipals}
          availableEstates={availableEstates}
          lawFirms={lawFirms}
          attorneys={attorneys}
          internalUsers={internalUsers}
          role={role}
        />

        <PgBondsCancellationDialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
          selectedBond={selectedBondToCancel}
          cancellationFile={cancellationFile}
          isCancelling={isCancelling}
          onFileChange={(e) => {
            const file = e.target.files[0];
            if (!validatePgBondDocumentFile(file, toast.error)) {
              e.target.value = "";
              return;
            }
            setCancellationFile(file);
            if (file) toast.success("File selected successfully!");
          }}
          onSubmit={async () => {
            if (!cancellationFile || !selectedBondToCancel) return;
            setIsCancelling(true);
            const formData = new FormData();
            formData.append("cancellation_file", cancellationFile);
            try {
              await apiClient(
                `/cancel-bond/`,
                "POST",
                formData,
                { bond_id: selectedBondToCancel.bond_id },
                { "Content-Type": "multipart/form-data" }
              );
              toast.success("Bond cancelled successfully!");
              setCancelDialogOpen(false);
              await fetchInitialData();
            } catch (error) {
              console.error(error);
              toast.error(error.message || "Failed to cancel the bond.");
            } finally {
              setIsCancelling(false);
            }
          }}
        />

        <PgBondsEndorsementDialog
          open={endorsementDialogOpen}
          onClose={() => setEndorsementDialogOpen(false)}
          selectedBond={selectedBondToEndorse}
          endorsementType={endorsementType}
          setEndorsementType={setEndorsementType}
          newBondAmount={newBondAmount}
          setNewBondAmount={setNewBondAmount}
          endorsementFile={endorsementFile}
          setEndorsementFile={setEndorsementFile}
          isSubmittingEndorsement={isSubmittingEndorsement}
          contactName={contactName}
          setContactName={setContactName}
          contactEmail={contactEmail}
          setContactEmail={setContactEmail}
          updatedName={updatedName}
          setUpdatedName={setUpdatedName}
          counties={counties}
          endorsementToCounty={endorsementToCounty}
          setEndorsementToCounty={setEndorsementToCounty}
          loadingCounties={loadingCounties}
          estimatedAssets={estimatedAssets}
          setEstimatedAssets={setEstimatedAssets}
          dwbFirstName={dwbFirstName}
          setDwbFirstName={setDwbFirstName}
          dwbMiddleName={dwbMiddleName}
          setDwbMiddleName={setDwbMiddleName}
          dwbLastName={dwbLastName}
          setDwbLastName={setDwbLastName}
          dwbSuffix={dwbSuffix}
          setDwbSuffix={setDwbSuffix}
          dwbAlias={dwbAlias}
          setDwbAlias={setDwbAlias}
          endorsementPrincipalId={endorsementPrincipalId}
          setEndorsementPrincipalId={setEndorsementPrincipalId}
          role={role}
          onReleaseRider={handleReleaseRider}
          onSubmitEndorsement={handleSubmitEndorsement}
          onFileSelect={(e) => {
            const file = e.target.files[0];
            if (!validatePgBondDocumentFile(file, toast.error)) {
              e.target.value = "";
              return;
            }
            setEndorsementFile(file);
            toast.success("File selected successfully!");
          }}
        />
      </Box>
    </>
  );
}

export default ProfessionalGuardianViewBonds;
